// src/lib/rpc/user/onboarding.ts
// Canonical onboarding RPC facade. Single immutable contract mapped to `public.onboarding_user()`.
//
// CANON v1.3.0 - TASK-001A: Contract Documentation
//
// This module provides the canonical interface for onboarding state management.
// All onboarding state decisions should be made based on `OnboardingResponse` from `getOnboarding()`.
//
// Contract:
// - `getOnboarding()` calls RPC `onboarding_user()` which returns normalized `OnboardingResponse`
// - `OnboardingResponse` contains:
//   - `state`: Current onboarding state (needs_profile, start_kyc, approved, etc.)
//   - `next_route`: Recommended route for user (/onboarding/wizard, /dashboard, etc.)
//   - `onboarding_completed`: Boolean flag indicating if onboarding is complete
//   - `onboarding_mode`: User's selected mode ('full' | 'receive_only' | null)
//   - `flags`: Additional flags (has_profile, is_admin, profile_completed)
//
// Usage:
// - After magic link authentication, call `getOnboarding()` to determine next step
// - If `onboarding_completed === false` → redirect to `/onboarding/wizard`
// - If `onboarding_completed === true` → redirect to `/dashboard`
// - Never use query params or localStorage for onboarding state decisions

import type { Database } from '@/integrations/supabase/types';
import { isAdminDomain } from '@/lib/domains/admin';
import { publicCallSingle, type LegacySingle } from '@/lib/rpc/public';
import { supabaseBrowser } from '@/lib/supabase-browser';
import type { PostgrestSingleResponse, Session, SupabaseClient } from '@supabase/supabase-js';

export type OnboardingState =
  | 'start_kyc'
  | 'resume_kyc'
  | 'pending_review'
  | 'approved'
  | 'rejected'
  | 'blocked'
  | 'needs_profile'
  | 'under_review'
  | 'awaiting_questionnaire'
  | 'awaiting_ubo'
  | 'incomplete'
  | 'link_created'
  | 'post_submission';  // NEW: Form submitted, awaiting review (incomplete without active link)

export type OnboardingRoute =
  | '/kyc/start'
  | '/kyc/resume'
  | '/kyc/pending'
  | '/dashboard'
  | '/blocked'
  | '/onboarding/profile'
  | '/onboarding/wizard';

/**
 * OnboardingResponse - Canonical onboarding state contract (CANON v1.3.0, TASK-001A)
 *
 * This is the single source of truth for onboarding state. All routing decisions
 * should be based on this response, not query params or localStorage.
 *
 * Key fields:
 * - `state`: Current onboarding state (determines what step user is on)
 * - `next_route`: Recommended route (used by Verify.tsx for redirects)
 * - `onboarding_completed`: If true, user has completed onboarding → redirect to /dashboard
 * - `onboarding_mode`: User's selected path ('full' | 'receive_only' | null)
 * - `flags.profile_completed`: Explicit flag for profile completion (used by useOnboardingWizard)
 */
export type OnboardingResponse = {
  state: OnboardingState;
  next_route: OnboardingRoute;
  reason: string | null;
  account_type?: string | null;  // 'individual' | 'business' | null
  full_name?: string | null;     // For personalization
  first_name?: string | null;
  last_name?: string | null;
  business_name?: string | null;
  email?: string | null;
  tos_status?: string | null;
  tos_link?: string | null;
  bridge_customer_id?: string | null;
  kyc_status?: string | null;
  kyc_link?: string | null;
  onboarding_mode?: 'full' | 'receive_only' | null;
  onboarding_completed?: boolean;
  customer_id?: string | null;  // Customer UUID for debugging
  flags: {
    has_profile: boolean;
    is_admin: boolean;
    profile_completed?: boolean;  // Explicit profile completion flag
  };
  trace_id: string;
};

type RawOnboardingResponse = Record<string, unknown>;

const VALID_STATES: ReadonlySet<OnboardingState> = new Set([
  'start_kyc',
  'resume_kyc',
  'pending_review',
  'approved',
  'rejected',
  'blocked',
  'needs_profile',
  'under_review',
  'awaiting_questionnaire',
  'awaiting_ubo',
  'incomplete',
  'post_submission',  // NEW: Form submitted, awaiting review
  'link_created'
]);

const VALID_ROUTES: ReadonlySet<OnboardingRoute> = new Set([
  '/kyc/start',
  '/kyc/resume',
  '/kyc/pending',
  '/dashboard',
  '/blocked',
  '/onboarding/profile',
  '/onboarding/wizard'
]);

function isBoolean(value: unknown): value is boolean {
  return typeof value === 'boolean';
}

export function isOnboardingResponse(payload: unknown): payload is OnboardingResponse {
  if (!payload || typeof payload !== 'object') return false;
  const record = payload as RawOnboardingResponse;

  const state = record['state'];
  const nextRoute = record['next_route'];
  const reason = record['reason'];
  const traceId = record['trace_id'];
  const flags = record['flags'];

  if (typeof state !== 'string' || !VALID_STATES.has(state as OnboardingState)) {
    return false;
  }
  if (typeof nextRoute !== 'string' || !VALID_ROUTES.has(nextRoute as OnboardingRoute)) {
    return false;
  }
  if (!(reason === null || typeof reason === 'string')) {
    return false;
  }
  if (typeof traceId !== 'string' || traceId.length === 0) {
    return false;
  }
  if (!flags || typeof flags !== 'object') {
    return false;
  }
  const hasProfile = (flags as Record<string, unknown>)['has_profile'];
  const isAdmin = (flags as Record<string, unknown>)['is_admin'];
  if (!isBoolean(hasProfile) || !isBoolean(isAdmin)) {
    return false;
  }

  return true;
}

function coerceFlags(raw: unknown): OnboardingResponse['flags'] {
  if (raw && typeof raw === 'object') {
    const record = raw as Record<string, unknown>;
    if (isBoolean(record.has_profile) && isBoolean(record.is_admin)) {
      return { 
        has_profile: record.has_profile, 
        is_admin: record.is_admin,
        // FIX: Also copy profile_completed from backend
        profile_completed: isBoolean(record.profile_completed) ? record.profile_completed : record.has_profile,
      };
    }
  }
  return { has_profile: false, is_admin: false, profile_completed: false };
}

function inferRoute(state: OnboardingState): OnboardingRoute {
  switch (state) {
    case 'approved':
      return '/dashboard';
    case 'needs_profile':
      return '/onboarding/profile';
    case 'blocked':
      return '/blocked';
    // All KYC-related states route to the unified onboarding wizard
    case 'start_kyc':
    case 'resume_kyc':
    case 'pending_review':
    case 'under_review':
    case 'rejected':
    case 'awaiting_questionnaire':
    case 'awaiting_ubo':
    case 'incomplete':
    case 'link_created':
      return '/onboarding/wizard';
    default:
      return '/onboarding/wizard';
  }
}

function normalizePayload(payload: unknown): OnboardingResponse {
  if (isOnboardingResponse(payload)) {
    return payload;
  }

  if (!payload || typeof payload !== 'object') {
    throw new Error('Invalid onboarding payload: not an object');
  }

  const record = payload as RawOnboardingResponse;
  const stateRaw = typeof record.state === 'string' ? (record.state as OnboardingState) : undefined;
  const state = VALID_STATES.has(stateRaw as OnboardingState) ? (stateRaw as OnboardingState) : 'start_kyc';
  const nextRouteRaw = typeof record.next_route === 'string' ? (record.next_route as OnboardingRoute) : undefined;
  const nextRoute = nextRouteRaw && VALID_ROUTES.has(nextRouteRaw) ? nextRouteRaw : inferRoute(state);
  const reason = typeof record.reason === 'string' ? record.reason : null;
  const traceId = typeof record.trace_id === 'string' && record.trace_id.length > 0 ? record.trace_id : crypto.randomUUID();
  const flags = coerceFlags(record.flags);

  // FIXED: Preserve tos_status from RPC response for ToSWaitingPage polling
  const tosStatus = typeof record.tos_status === 'string' ? record.tos_status : null;
  const tosLink = typeof record.tos_link === 'string' ? record.tos_link : null;
  // Also preserve other optional fields
  const accountType = typeof record.account_type === 'string' ? record.account_type : null;
  const fullName = typeof record.full_name === 'string' ? record.full_name : null;
  const email = typeof record.email === 'string' ? record.email : null;
  const firstName = typeof record.first_name === 'string' ? record.first_name : null;
  const lastName = typeof record.last_name === 'string' ? record.last_name : null;
  const businessName = typeof record.business_name === 'string' ? record.business_name : null;
  const bridgeCustomerId = typeof record.bridge_customer_id === 'string' ? record.bridge_customer_id : null;
  const kycStatus = typeof record.kyc_status === 'string' ? record.kyc_status : null;
  const kycLink = typeof record.kyc_link === 'string' ? record.kyc_link : null;
  const onboardingModeRaw = typeof record.onboarding_mode === 'string' ? record.onboarding_mode : null;
  const onboardingMode =
    onboardingModeRaw === 'full' || onboardingModeRaw === 'receive_only' ? onboardingModeRaw : null;
  const onboardingCompleted =
    typeof record.onboarding_completed === 'boolean'
      ? record.onboarding_completed
      : (state === 'approved' && Boolean(onboardingMode));

  return {
    state,
    next_route: nextRoute,
    reason,
    tos_status: tosStatus,
    tos_link: tosLink,
    account_type: accountType,
    full_name: fullName,
    first_name: firstName,
    last_name: lastName,
    business_name: businessName,
    email,
    bridge_customer_id: bridgeCustomerId,
    kyc_status: kycStatus,
    kyc_link: kycLink,
    onboarding_mode: onboardingMode,
    onboarding_completed: onboardingCompleted,
    flags,
    trace_id: traceId
  };
}

function isBrowserClient(client: SupabaseClient<Database>): boolean {
  return client === supabaseBrowser;
}

async function ensureBrowserSession(client: SupabaseClient<Database>) {
  if (typeof window === 'undefined') return;
  if (!isBrowserClient(client)) return;

  const pollSession = async (): Promise<Session | null> => {
    const { data } = await client.auth.getSession();
    return data.session ?? null;
  };

  const waitForSession = async (): Promise<Session | null> => {
    for (let attempt = 0; attempt < 15; attempt++) {
      const session = await pollSession();
      if (session) {
        return session;
      }
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    return null;
  };

  let session = await waitForSession();
  if (!session) {
    throw new Error('[onboarding_user] no browser session');
  }

  if (session.access_token) {
    return;
  }

  const { error: refreshError } = await client.auth.refreshSession();
  if (refreshError) {
    throw refreshError;
  }

  session = await waitForSession();
  if (!session?.access_token) {
    throw new Error('[auth] no JWT after refresh');
  }
}

function extractErrorMessage(error: unknown): string {
  if (!error) return '';
  if (typeof error === 'string') return error;
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string') {
      return message;
    }
  }
  try {
    return String(error);
  } catch {
    return '';
  }
}

export function isAuthRetryableError(error: unknown): boolean {
  const normalized = extractErrorMessage(error).toLowerCase();
  if (!normalized) return false;
  return normalized.includes('auth only') || normalized.includes('authentication required') || normalized.includes('auth required');
}

function shouldRetryAuthOnly(error: unknown): boolean {
  return isAuthRetryableError(error);
}

async function invokeOnboarding(client: SupabaseClient<Database>) {
  return client.rpc('onboarding_user');
}

type OnboardingOptions = {
  allowAdminContext?: boolean;
};

export async function callOnboardingUser<T = unknown>(
  client: SupabaseClient<Database> = supabaseBrowser,
  options: OnboardingOptions = {}
): Promise<PostgrestSingleResponse<T>> {
  const { allowAdminContext = false } = options;

  if (typeof window !== 'undefined' && isAdminDomain() && !allowAdminContext) {
    throw new Error('onboarding_user is public-only');
  }

  await ensureBrowserSession(client);

  let response = await invokeOnboarding(client);

  if (response.error && shouldRetryAuthOnly(response.error)) {
    await new Promise(resolve => setTimeout(resolve, 150));
    await ensureBrowserSession(client);
    response = await invokeOnboarding(client);
  }

  return response as PostgrestSingleResponse<T>;
}

/**
 * getOnboarding - Canonical function to get user's onboarding state (CANON v1.3.0, TASK-001A)
 *
 * This is the primary function for determining onboarding state after authentication.
 * Used by:
 * - Verify.tsx: After magic link auth, to determine redirect destination
 * - useOnboardingWizard: To get current step and state
 * - CallbackPage: To determine post-auth flow
 *
 * Contract:
 * - Calls RPC `onboarding_user()` which returns normalized `OnboardingResponse`
 * - Handles auth retries automatically (for "auth only" errors)
 * - Returns normalized response with all required fields
 *
 * Redirect logic (TASK-001A):
 * - If `onboarding_completed === false` → redirect to `/onboarding/wizard`
 * - If `onboarding_completed === true` → redirect to `/dashboard`
 * - If `state === 'approved'` → force redirect to `/dashboard` (even if next_route says otherwise)
 *
 * @param client - Supabase client (defaults to browser client)
 * @param options - Options including `allowAdminContext` for admin domains
 * @returns Normalized OnboardingResponse
 */
export async function getOnboarding(
  client: SupabaseClient<Database> = supabaseBrowser,
  options: OnboardingOptions = {}
): Promise<OnboardingResponse> {
  const { data, error } = await callOnboardingUser(client, options);

  if (error) {
    const err = new Error(`[onboarding_user] ${error.message}`);
    (err as Error & { cause?: unknown }).cause = error;
    throw err;
  }

  return normalizePayload(data);
}

export function routeFromState(state: OnboardingState): OnboardingRoute {
  return inferRoute(state);
}

export type OnboardingStatusV2 = never;
export type OnboardingStatus = never;

export async function getCustomerIdForAuth(authUid?: string): Promise<LegacySingle<string>> {
  const args = typeof authUid === 'string' && authUid.length > 0 ? { _auth_uid: authUid } : {};
  return publicCallSingle('get_customer_id_for_auth', args);
}


export async function onboardingBootstrap(): Promise<LegacySingle<OnboardingResponse>> {
  try {
    const data = await getOnboarding();
    return { data, error: null };
  } catch (error) {
    return {
      data: null,
      error: error instanceof Error ? { message: error.message, code: 'ERR_RPC' } : { message: 'Unknown error', code: 'ERR_RPC' }
    };
  }
}

export async function kycStart(params: { redirect_uri: string }): Promise<LegacySingle<{ url: string; allowed_hosts?: string[] }>> {
  const { redirect_uri } = params;

  return publicCallSingle<{ url: string; allowed_hosts?: string[] }>('kyc_start_user', {
    _redirect_uri: redirect_uri
  });
}

export async function acceptTermsOfService(): Promise<LegacySingle<{ success: boolean; accepted_at?: string }>> {
  return publicCallSingle('onboarding_accept_tos', {});
}
